import { apiService } from './api';
import type { RetestIncident, LabIncidentListItem, LabIncident, LabIncidentStatus, LabReport, Task } from '../types';

export interface LabIncidentListParams {
  facility_id?: string;
  status?: LabIncidentStatus;
}

export interface IncidentTaskPayload {
  description: string;
  responsible_user_id: string;
  due_by?: string;
  priority?: string;
}

export const labIncidentsService = {
  async retestIncidents(reportId: string): Promise<RetestIncident[]> {
    return apiService.get<RetestIncident[]>(`/lab-reports/${reportId}/retest-incidents`);
  },
  async setReportInclusion(id: string, include_in_report: boolean): Promise<LabIncident> {
    return apiService.patch<LabIncident>(`/lab-incidents/${id}`, { include_in_report });
  },
  async retestCandidates(id: string): Promise<LabReport[]> {
    return apiService.get<LabReport[]>(`/lab-incidents/${id}/retest-candidates`);
  },

  async get(id: string): Promise<LabIncident> {
    return apiService.get<LabIncident>(`/lab-incidents/${id}`);
  },

  async list(params?: LabIncidentListParams): Promise<LabIncidentListItem[]> {
    const query: Record<string, string> = {};
    if (params?.facility_id) query.facility_id = params.facility_id;
    if (params?.status) query.status = params.status;
    return apiService.get<LabIncidentListItem[]>('/lab-incidents/', query);
  },

  async acknowledge(id: string): Promise<LabIncident> {
    return apiService.post<LabIncident>(`/lab-incidents/${id}/acknowledge`, {});
  },

  async createTask(id: string, payload: IncidentTaskPayload): Promise<Task> {
    return apiService.post<Task>(`/lab-incidents/${id}/tasks`, payload);
  },

  async orderRetest(id: string, notes?: string): Promise<LabIncident> {
    return apiService.post<LabIncident>(`/lab-incidents/${id}/order-retest`, { notes });
  },

  async linkRetest(id: string, labReportId: string): Promise<LabIncident> {
    return apiService.post<LabIncident>(`/lab-incidents/${id}/link-retest`, {
      lab_report_id: labReportId,
    });
  },

  async close(id: string): Promise<LabIncident> {
    return apiService.post<LabIncident>(`/lab-incidents/${id}/close`, {});
  },

  async reissue(id: string, details?: string): Promise<LabIncident> {
    return apiService.post<LabIncident>(`/lab-incidents/${id}/reissue`, { details });
  },

  async open(labReportId: string): Promise<LabIncident> {
    return apiService.post<LabIncident>('/lab-incidents/', { lab_report_id: labReportId });
  },
};
